import styled from 'styled-components';
import { Loader } from '@shared/ui/Progress';
import { useGetContractAuditQuery } from '@shared/api';
import type { AuditEntry } from '@entities/legal/types';

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
  padding: ${({ theme }) => theme.spacing(3)} 0;
`;

const Line = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  line-height: 1.45;

  &:last-child {
    border-bottom: none;
  }
`;

const When = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Action = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Who = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-weight: 400;
  font-size: 12px;
  margin-left: 8px;
`;

const Details = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const BATCH_APPENDED = 'statut.contract.batch_appended';

const ACTION_LABELS: Record<string, string> = {
  'statut.contract.upload': 'Загружен файл договора',
  'statut.contract.version_added': 'Добавлена новая версия',
  'statut.contract.analyzed': 'Завершён LLM-анализ',
  'statut.contract.schedule_analysis': 'Запущен повторный анализ',
  'statut.contract.force_analyze': 'Запущен анализ большого комплекта',
  'statut.contract.delete': 'Удалён договор',
  'statut.contract.create': 'Создана запись договора',
  'statut.contract.auto_created': 'Договор заведён из письма',
  'statut.contract.patch': 'Отредактированы метаданные',
  'statut.contract.diff': 'Сравнение версий',
  'statut.contract.conclusion_export': 'Скачано юридическое заключение',
  [BATCH_APPENDED]: 'Догружены файлы комплекта',
  'statut.contract.files_appended': 'Догружены файлы — новая версия',
  'statut.contract.compose_protocol': 'Составлен протокол разногласий',
  'statut.contract.handoff': 'Договор введён в работу',
  'statut.contract.handoff_ack': 'Служба подтвердила ввод в работу',
  'statut.contract.set_project': 'Договор привязан к объекту',
  'statut.contract.expires_at_auto': 'Срок действия определён из текста',
  'statut.contract.analysis_failed': 'Анализ не завершён',
  'statut.contract.number_cleared': 'Номер договора очищен',
  'statut.contract.audit.confirmed': 'Учтены замечания внешнего аудита',
  'statut.risk.annotate': 'Решение по риску',
  'statut.risk.clear_annotation': 'Сброс решения по риску',
  'statut.deadline.create': 'Создан срок',
  'statut.deadline.overdue': 'Срок просрочен',
  'statut.deadline.notification': 'Напоминание о сроке',
  'statut.deadline.closed': 'Срок отмечен выполненным',
  'statut.deadline.reopened': 'Срок возвращён в работу',
  'statut.deadline.delete': 'Срок удалён',
};

/** Неизвестное действие — не служебный код (`statut.contract.…`), который
 *  юристу ничего не говорит; сам код остаётся во всплывающей подсказке. */
function labelFor(action: string): string {
  return ACTION_LABELS[action] ?? 'Действие в системе';
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Большой комплект клиент досылает пачками, и каждая пачка — отдельная
 *  запись журнала: 22.10.2026 пакет на 156 файлов дал 12 одинаковых строк
 *  подряд. Склеиваем соседние пачки в одну строку с суммой. Журнал идёт от
 *  новых к старым, время строки — последней пачки. */
function mergeBatches(items: AuditEntry[]): AuditEntry[] {
  const out: AuditEntry[] = [];
  for (const e of items) {
    const prev = out[out.length - 1];
    if (e.action === BATCH_APPENDED && prev?.action === BATCH_APPENDED) {
      const pd = prev.details ?? {};
      const ed = e.details ?? {};
      prev.details = {
        ...pd,
        added: num(pd.added) + num(ed.added),
        total: Math.max(num(pd.total), num(ed.total)),
        finalized: pd.finalized === true || ed.finalized === true,
        batches: num(pd.batches, 1) + 1,
      };
      continue;
    }
    out.push(
      e.action === BATCH_APPENDED ? { ...e, details: { ...(e.details ?? {}), batches: 1 } } : e,
    );
  }
  return out;
}

function fmtDt(s: string): string {
  return new Date(s).toLocaleString('ru-RU');
}

function shortDetails(e: AuditEntry): string | null {
  const d = e.details;
  if (!d) return null;
  const out: string[] = [];
  if (typeof d.decision === 'string') {
    const labels: Record<string, string> = {
      accepted: 'принят',
      rejected: 'отклонён',
      disputed: 'под вопросом',
      cleared: 'сброшен',
    };
    out.push(`решение: ${labels[d.decision] ?? d.decision}`);
  }
  if (typeof d.comment === 'string' && d.comment) {
    out.push(`«${d.comment}»`);
  }
  if (typeof d.risks_count === 'number') {
    out.push(
      e.action === 'statut.contract.compose_protocol'
        ? `пунктов в протоколе: ${d.risks_count}`
        : `найдено рисков: ${d.risks_count}`,
    );
  }
  if (typeof d.added === 'number') {
    out.push(`добавлено файлов: ${d.added}`);
  }
  if (Array.isArray(d.appended)) {
    out.push(`добавлено файлов: ${d.appended.length}`);
  }
  if (typeof d.total === 'number') {
    out.push(`всего в комплекте: ${d.total}`);
  }
  if (typeof d.batches === 'number' && d.batches > 1) {
    out.push(`пачек: ${d.batches}`);
  }
  if (d.finalized === true) {
    out.push('комплект собран — запущен разбор');
  }
  if (typeof d.reparse_files === 'number') {
    out.push(`сначала разбор файлов без текста: ${d.reparse_files}`);
  }
  if (typeof d.deadlines_count === 'number') {
    out.push(`сроков: ${d.deadlines_count}`);
  }
  if (typeof d.deviations_count === 'number' && d.deviations_count > 0) {
    out.push(`отклонений от шаблона: ${d.deviations_count}`);
  }
  if (typeof d.version_no === 'number') {
    out.push(`версия v${d.version_no}`);
  }
  if (typeof d.source === 'string') {
    out.push(`источник: ${d.source === 'counterparty' ? 'от контрагента' : 'наша'}`);
  }
  if (typeof d.kind === 'string') {
    out.push(`тип: ${d.kind}`);
  }
  if (e.action === 'statut.contract.analysis_failed' && typeof d.reason === 'string') {
    out.push(ANALYSIS_FAILURE_REASONS[d.reason] ?? 'внутренняя ошибка');
  }
  if (e.action === 'statut.contract.number_cleared' && typeof d.old === 'string') {
    out.push(`было «${d.old}» — слово из текста, а не номер`);
  }
  return out.length > 0 ? out.join(' · ') : null;
}

/** `details.reason` у «Анализ не завершён» (parse_report.rs, analysis_failure_kind). */
const ANALYSIS_FAILURE_REASONS: Record<string, string> = {
  llm_unavailable: 'ИИ не ответил',
  llm_budget: 'исчерпан дневной лимит расходов на ИИ',
  llm_disabled: 'ИИ недоступен',
  llm_bad_json: 'ответ модели в неверном формате',
  internal: 'внутренняя ошибка',
};

interface Props {
  contractId: string;
}

export function ContractAudit({ contractId }: Props) {
  const { data, isLoading } = useGetContractAuditQuery(contractId);
  if (isLoading) return <Loader />;
  const items = data?.items ?? [];
  if (items.length === 0) {
    return <Empty>История по этому договору пока пуста.</Empty>;
  }
  return (
    <div>
      {mergeBatches(items).map((e) => {
        const sd = shortDetails(e);
        return (
          <Line key={e.id}>
            <When>{fmtDt(e.created_at)}</When>
            <div>
              <Action title={e.action}>
                {labelFor(e.action)}
                {e.user_email && <Who>· {e.user_email}</Who>}
              </Action>
              {sd && <Details>{sd}</Details>}
            </div>
          </Line>
        );
      })}
    </div>
  );
}
